import React, { useEffect } from "react";
import {
    AppBar,
    Toolbar,
    IconButton,
    Typography,
    Box,
    Avatar,
    Menu,
    MenuItem,
    Badge
} from "@mui/material";
import LogoutIcon from "@mui/icons-material/Logout";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { useDispatch, useSelector } from "react-redux";
import { logoutUser } from "store/authReducer";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import * as Icons from "@mui/icons-material";

const useCurrentModule = () => {
    const location = useLocation();
    const { modules = {} } = useSelector((state) => state.auth);

    for (const [groupKey, items] of Object.entries(modules)) {
        const found = items.find(m =>
            location.pathname.startsWith(m.route)
        );
        if (found) {
            return {
                ...found,
                group: groupKey
            };
        }
    }
    return null;
};

const Header = ({ open, isMobile, drawerWidth = 260 }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { user, notifications = [] } = useSelector((state) => state.auth);

    const [userAnchor, setUserAnchor] = React.useState(null);
    const [notifAnchor, setNotifAnchor] = React.useState(null);

    const currentModule = useCurrentModule();

    useEffect(() => {
        if (currentModule?.name) {
            document.title = `${currentModule.name} | SAR Admin`;
        } else {
            document.title = "SAR Admin";
        }
    }, [currentModule?.name]);

    const handleLogout = () => {
        dispatch(logoutUser());
        navigate("/login");
    };

    const ModuleIcon = Icons[currentModule?.icon] || Icons.FiberManualRecord;

    return (
        <AppBar
            position="fixed"
            elevation={0}
            sx={{
                background: "linear-gradient(90deg, #020617, #0f172a)",
                borderBottom: "1px solid rgba(255,255,255,0.08)",
                zIndex: theme => theme.zIndex.drawer + 1,

                ml: !isMobile && open ? `${drawerWidth}px` : 0,
                width: !isMobile && open
                    ? `calc(100% - ${drawerWidth}px)`
                    : "100%",

                transition: theme =>
                    theme.transitions.create(["margin-left", "width"], {
                        easing: theme.transitions.easing.sharp,
                        duration: theme.transitions.duration.standard
                    })
            }}
        >
            <Toolbar sx={{ minHeight: 64, px: 3 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <AnimatePresence mode="wait">
                        {ModuleIcon && (
                            <motion.div
                                key={currentModule?.route}
                                initial={{ opacity: 0, scale: 0.85, y: 6 }}
                                animate={{ opacity: 1, scale: 1, y: 0 }}
                                exit={{ opacity: 0, scale: 0.85, y: -6 }}
                                transition={{ duration: 0.25 }}
                            >
                                <ModuleIcon
                                    fontSize="medium"
                                    style={{ color: "#e5e7eb" }}
                                />
                            </motion.div>
                        )}
                    </AnimatePresence>

                    <AnimatePresence mode="wait">
                        <motion.div
                            key={currentModule?.name || "default"}
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -8 }}
                            transition={{ duration: 0.25 }}
                        >
                            <Typography variant="caption" sx={{ color: "#94a3b8" }}>
                                {currentModule?.group?.toUpperCase()}
                            </Typography>

                            <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                {currentModule?.name || "SAR Admin"}
                            </Typography>
                        </motion.div>
                    </AnimatePresence>
                </Box>

                <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 1 }}>
                    <IconButton
                        onClick={(e) => setNotifAnchor(e.currentTarget)}
                        sx={{
                            color: "#e5e7eb",
                            "&:hover": { backgroundColor: "rgba(255,255,255,0.08)" }
                        }}
                    >
                        <Badge badgeContent={notifications.length} color="error">
                            <NotificationsNoneIcon />
                        </Badge>
                    </IconButton>

                    <Menu
                        anchorEl={notifAnchor}
                        open={Boolean(notifAnchor)}
                        onClose={() => setNotifAnchor(null)}
                        PaperProps={{
                            sx: {
                                mt: 1,
                                minWidth: 280,
                                backgroundColor: "#020617",
                                color: "#e5e7eb",
                                borderRadius: 2,
                                border: "1px solid rgba(255,255,255,0.08)"
                            }
                        }}
                    >
                        {notifications.length === 0 && (
                            <MenuItem disabled>
                                <Typography sx={{ fontSize: 12, color: "#94a3b8" }}>
                                    No hay notificaciones
                                </Typography>
                            </MenuItem>
                        )}

                        {notifications.map((n, i) => (
                            <MenuItem key={i}>
                                <Typography sx={{ fontSize: 13 }}>
                                    {n.message}
                                </Typography>
                            </MenuItem>
                        ))}
                    </Menu>

                    <IconButton
                        onClick={(e) => setUserAnchor(e.currentTarget)}
                        sx={{
                            color: "#e5e7eb",
                            px: 1.5,
                            borderRadius: 2,
                            "&:hover": { backgroundColor: "rgba(255,255,255,0.08)" }
                        }}
                    >
                        <Avatar
                            sx={{
                                width: 32,
                                height: 32,
                                bgcolor: "primary.main",
                                fontSize: 14
                            }}
                        >
                            {user?.email?.[0]?.toUpperCase() || "U"}
                        </Avatar>

                        <Box sx={{ mx: 1, textAlign: "left" }}>
                            <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                                {user?.email}
                            </Typography>
                            <Typography sx={{ fontSize: 11, color: "#94a3b8" }}>
                                {user?.role}
                            </Typography>
                        </Box>

                        <KeyboardArrowDownIcon />
                    </IconButton>

                    <Menu
                        anchorEl={userAnchor}
                        open={Boolean(userAnchor)}
                        onClose={() => setUserAnchor(null)}
                        PaperProps={{
                            sx: {
                                mt: 1,
                                minWidth: 180,
                                backgroundColor: "#020617",
                                color: "#e5e7eb",
                                borderRadius: 2,
                                border: "1px solid rgba(255,255,255,0.08)"
                            }
                        }}
                    >
                        <MenuItem onClick={handleLogout}>
                            <LogoutIcon fontSize="small" sx={{ mr: 1 }} />
                            Cerrar sesión
                        </MenuItem>
                    </Menu>
                </Box>
            </Toolbar>
        </AppBar>
    );
};

export default Header;
